import { Card, Col, Form, Row, Select, Space, Button, Spin } from "antd";
import HandleNotification from "common/Notification";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LINE_WISE_OPERATIONS_ENDPOINT,
  GET_SEWING_LINES,
  ASSIGNED_OPERATORS_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "../../../apiServices/common";
import { alertPop } from "../../../apiServices/common/helper";
import { isArrayAndHasValue } from "../../../utils/functions";
import CustomDatePicker from "../../../common/CustomDatePicker";
import moment from "moment";

const HourlyOperationTracking = forwardRef((props, ref) => {
  const { info, view, mode } = props;
  const [lineList, setLineList] = useState([]);
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [selectedOperators, setSelectedOperators] = useState([]);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { Option } = Select;

  // Fetch line list
  const getLineList = useCallback(async () => {
    setLoadingLines(true);
    try {
      const response = await getData(GET_SEWING_LINES, false, { per_page: 1000 });
      if (response?.data?.code === 200) {
        setLineList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching lines:", error);
    } finally {
      setLoadingLines(false);
    }
  }, []);

  // Fetch assigned operators
  const getAssignedOperators = useCallback(async (queryDate, lineId) => {
    if (!queryDate || !lineId) {
      setAssignedOperators([]);
      return;
    }

    setLoadingOperators(true);
    try {
      const response = await getData(ASSIGNED_OPERATORS_ENDPOINT, false, {
        date: moment(queryDate).format("YYYY-MM-DD"),
        line_id: lineId,
      });

      if (response?.data?.code === 200) {
        const operators = response.data.data || [];
        setAssignedOperators(operators);
      }
    } catch (error) {
      console.error("Error fetching assigned operators:", error);
      setAssignedOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  }, []);

  // Handle query date change
  const handleQueryDateChange = (date) => {
    const lineId = form.getFieldValue("line_id");
    if (date && lineId) {
      getAssignedOperators(date, lineId);
      setSelectedOperators([]);
      form.setFieldValue("operation_ids", []);
    } else {
      setAssignedOperators([]);
      setSelectedOperators([]);
      form.setFieldValue("operation_ids", []);
    }
  };

  // Handle line change
  const handleLineChange = (lineId) => {
    const queryDate = form.getFieldValue("query_date");
    if (queryDate && lineId) {
      getAssignedOperators(queryDate, lineId);
      setSelectedOperators([]);
      form.setFieldValue("operation_ids", []);
    } else {
      setAssignedOperators([]);
      setSelectedOperators([]);
      form.setFieldValue("operation_ids", []);
    }
  };

  // Handle operator selection change
  const handleOperatorChange = (value) => {
    if (value.includes("select_all")) {
      const allIds = assignedOperators.map((op) => op.id).filter(Boolean);
      setSelectedOperators(allIds);
      form.setFieldValue("operation_ids", allIds);
    } else if (value.includes("unselect_all")) {
      setSelectedOperators([]);
      form.setFieldValue("operation_ids", []);
    } else {
      setSelectedOperators(value);
      form.setFieldValue("operation_ids", value);
    }
  };

  // Select all operators
  const selectAllOperators = () => {
    const allIds = assignedOperators.map((op) => op.id).filter(Boolean);
    setSelectedOperators(allIds);
    form.setFieldValue("operation_ids", allIds);
  };

  // Unselect all operators
  const unselectAllOperators = () => {
    setSelectedOperators([]);
    form.setFieldValue("operation_ids", []);
  };

  // Check if all operators are selected
  const isAllSelected = () => {
    if (!isArrayAndHasValue(assignedOperators)) return false;
    const allIds = assignedOperators.map((op) => op.id).filter(Boolean);
    return allIds.length > 0 && allIds.every((id) => selectedOperators.includes(id));
  };

  // Load initial data for edit/view mode
  useEffect(() => {
    if (info && (view || mode === "edit")) {
      setLoadingForm(true);
      const formValues = {
        query_date: info.query_date ? moment(info.query_date) : moment(),
        line_id: info.line_id,
      };

      // Set selected operators from line_wise_operation_details
      if (isArrayAndHasValue(info.line_wise_operation_details)) {
        const operatorIds = info.line_wise_operation_details.map((detail) => detail.d_l_p_s_detail_id).filter(Boolean);
        setSelectedOperators(operatorIds);
        formValues.operation_ids = operatorIds;
      }

      form.setFieldsValue(formValues);

      // Fetch assigned operators for the given date and line
      if (info.query_date && info.line_id) {
        getAssignedOperators(info.query_date, info.line_id).finally(() => {
          setLoadingForm(false);
        });
      } else {
        setLoadingForm(false);
      }
    } else if (mode === "add") {
      // Set default query_date to today
      form.setFieldsValue({
        query_date: moment(),
      });
    }
  }, [info, view, mode, form, getAssignedOperators]);

  // Load line list on mount
  useEffect(() => {
    getLineList();
  }, [getLineList]);

  useImperativeHandle(ref, () => ({
    submit() {
      form
        .validateFields()
        .then(async (values) => {
          setLoadingForm(true);
          try {
            const payload = {
              query_date: moment(values.query_date).format("YYYY-MM-DD"),
              line_id: values.line_id,
              operation: (values.operation_ids || []).map((dLpsDetailId) => ({
                d_l_p_s_detail_id: dLpsDetailId,
              })),
            };

            let query = info?.id ? `${LINE_WISE_OPERATIONS_ENDPOINT}/${info?.id}` : LINE_WISE_OPERATIONS_ENDPOINT;
            const response = info?.id ? await putData(query, payload) : await postData(query, payload);
            console.log("Response after submit:", response);
            if (response && response?.code === 200) {
              HandleNotification(
                "success",
                "bottomRight",
                `Hourly Operation Tracking ${info?.id ? "updated" : "created"} successfully`,
                null,
              );
              navigate("/digitalization/hourly-operation-tracking");
            } else {
              HandleNotification(
                "error",
                "bottomRight",
                `Error ${info?.id ? "updating" : "creating"} Hourly Operation Tracking`,
                null,
              );
            }
          } catch (error) {
            console.error("Error submitting form:", error);
            HandleNotification(
              "error",
              "bottomRight",
              `Error ${info?.id ? "updating" : "creating"} Hourly Operation Tracking`,
              null,
            );
          } finally {
            setLoadingForm(false);
          }
        })
        .catch((errorInfo) => {
          alertPop("error", "Please fill all required fields");
        });
    },
    discart() {
      form.resetFields();
      setSelectedOperators([]);
      setAssignedOperators([]);
    },
  }));

  // Prepare operator options with select all/unselect all
  const operatorOptions = () => {
    const options = assignedOperators
      .map((op) => ({
        label: op.operation_info?.sam_data_bank?.name || "N/A",
        value: op.id, // Use d_l_p_s_detail_id as value
        disabled: false,
      }))
      .filter((opt) => opt.value); // Filter out invalid options

    const allSelected = isAllSelected();

    return [
      allSelected ? { label: "Unselect All", value: "unselect_all" } : { label: "Select All", value: "select_all" },
      ...options,
    ];
  };

  return (
    <Spin spinning={loadingForm || loadingLines}>
      <div className="containt-body">
        <Form disabled={view || mode === "view"} layout="vertical" form={form} name="control-hooks">
          <Card>
            <Row gutter={6}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Query Date"
                  name="query_date"
                  rules={[
                    {
                      required: true,
                      message: "Please select Query Date!",
                    },
                  ]}
                >
                  <CustomDatePicker
                    style={{ width: "100%" }}
                    size="small"
                    placeholder="Select Date"
                    onChange={handleQueryDateChange}
                    disabled={view || mode === "view"}
                  />
                </Form.Item>
              </Col>

              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Line"
                  name="line_id"
                  rules={[
                    {
                      required: true,
                      message: "Please select Line!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select a Line"
                    optionFilterProp="children"
                    size="small"
                    onChange={handleLineChange}
                    allowClear
                    disabled={view || mode === "view"}
                    loading={loadingLines}
                  >
                    {isArrayAndHasValue(lineList) &&
                      lineList.map((line) => (
                        <Option value={line.id} key={line.id}>
                          {line.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col className="gutter-row" span={16}>
                <Form.Item
                  label="Assigned Operations"
                  name="operation_ids"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one operator!",
                    },
                  ]}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Space>
                      <Button
                        type="primary"
                        size="small"
                        onClick={selectAllOperators}
                        disabled={!isArrayAndHasValue(assignedOperators) || loadingOperators || view || mode === "view"}
                      >
                        Select All
                      </Button>
                      <Button
                        danger
                        size="small"
                        onClick={unselectAllOperators}
                        disabled={!isArrayAndHasValue(assignedOperators) || loadingOperators || view || mode === "view"}
                      >
                        Unselect All
                      </Button>
                    </Space>
                    <Select
                      mode="multiple"
                      showSearch
                      placeholder={
                        loadingOperators
                          ? "Loading operators..."
                          : !form.getFieldValue("query_date") || !form.getFieldValue("line_id")
                          ? "Please select Query Date and Line first"
                          : "Select Operators"
                      }
                      optionFilterProp="children"
                      size="small"
                      value={selectedOperators}
                      onChange={handleOperatorChange}
                      disabled={
                        !form.getFieldValue("query_date") ||
                        !form.getFieldValue("line_id") ||
                        loadingOperators ||
                        view ||
                        mode === "view"
                      }
                      loading={loadingOperators}
                      style={{ width: "100%" }}
                      filterOption={(input, option) => {
                        return option?.label?.toLowerCase()?.includes(input?.toLowerCase());
                      }}
                      options={operatorOptions()}
                    />
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </div>
    </Spin>
  );
});

export default HourlyOperationTracking;
